<template>
  <SpPage :title="id ? '予約変更' : '新規予約'" :loading="busy">
    <div class="customer-type" :class="{ disabled: isBlock }">
      <CustomerTypeInput />
    </div>

    <FormSection title="日時・人数入力" required>
      <div class="form-item">
        <FormLabel itemName="予約日">
          <CalendarInput
            v-model="reserveDate"
            :holidayList="allHolidays"
            :businessDay="businessDay"
            :minDate="minDate"
            :maxDate="maxDate"
            :disabled="disableEditDateReservedByGnavi"
            :popperOptions="{ modifiers: { flip: { enabled: false }, preventOverflow: { enabled: false } } }"
            :reserveCalendar="reserveCalendar"
            :busy="busy"
            @setDate="setDate"
            @beforeShow="setDate(reserveDate)"
            isSP
          />
        </FormLabel>
      </div>
      <Validation
        v-if="disableEditDateReservedByGnavi"
        message="予約連携しているため予約日を変更できません"
        class="number-error"
      />

      <div class="form-item time">
        <FormLabel itemName="開始時間">
          <SelectBox :items="startTimeMap" v-model="reserveTime" :disabled="disableEditTimeReservedByGnavi" />
        </FormLabel>
        <span>〜</span>
        <FormLabel itemName="終了時間">
          <SelectBox :items="endTimeMap" v-model.number="utilizationTime" :disabled="disableEditTimeReservedByGnavi" />
        </FormLabel>
      </div>
      <Validation
        v-if="disableEditTimeReservedByGnavi"
        message="予約連携しているため予約時間を変更できません"
        class="number-error"
      />
      <div v-if="isBlockOverTime" class="block-error">
        ぐるなびネット予約のブロックは最長6時間になります（6時間を超える場合は分けて登録してください）
      </div>

      <div class="form-item couple">
        <FormLabel itemName="大人">
          <SelectBox :items="peopleMap" v-model.number="adults" :error="isNumberExceeded" :disabled="isBlock" />
        </FormLabel>
        <FormLabel itemName="子供">
          <SelectBox :items="peopleMap" v-model.number="children" :error="isNumberExceeded" :disabled="isBlock" />
        </FormLabel>
      </div>
      <Validation
        v-if="isNumberExceeded"
        message="予約連携しているため999名以上には変更できません"
        class="number-error"
      />
    </FormSection>

    <FormSection title="テーブル選択" required>
      <div class="form-item table">
        <div class="buttons">
          <Button small @click="tableSelectModal = true">選択</Button>
          <Button small @click="clearTables">クリア</Button>
        </div>
        <ul v-if="tableNameList.length" class="table-list">
          <li v-for="(tableName, key) in tableNameList" :key="key">{{ tableName }}</li>
        </ul>
        <ul v-if="tableSelectErrorMsg.length" class="error">
          <li v-for="(message, key) in tableSelectErrorMsg" :key="key">{{ message }}</li>
        </ul>
      </div>
    </FormSection>
    <TableSelectModal
      v-if="tableSelectModal"
      :reserveDate="reserveDate"
      :reserve="reserve"
      :reservesByTableId="reservesByTableId"
      :getDeficiency="getDeficiency"
      :getDuplicatedReserves="getDuplicatedReserves"
      :isTemporarySeat="!!this.detail.tableAllocationFlg"
      :isBlock="isBlock"
      :hasStaffList="isTableSerectOnly"
      :backTo="backTo"
      @close="isTableSerectOnly ? $router.push(backTo) : (tableSelectModal = false)"
      @select="updateTables"
    />

    <FormSection
      title="名前・電話番号"
      :required="!isBlock && !isWalkin"
      :requiredText="isBlock || isWalkin ? '' : 'お名前・電話番号いずれか'"
    >
      <FormLabel itemName="お名前">
        <template v-if="customer && customer.id" #aside>
          <div class="customer-clear" @click="clearCustomer"><ClearIcon width="16" height="16" />顧客情報クリア</div>
        </template>
        <div class="names">
          <TextInput v-model="lastNameKana" placeholder="せい" :disabled="isBlock" />
          <TextInput v-model="firstNameKana" placeholder="めい" :disabled="isBlock" />
          <Button small fit :disabled="!hasCustomerName" @click="searchName"><SearchIcon /></Button>
          <TextInput v-model="lastName" placeholder="姓" :disabled="isBlock" />
          <TextInput v-model="firstName" placeholder="名" :disabled="isBlock" />
        </div>
      </FormLabel>
      <FormLabel itemName="電話番号">
        <div class="tels">
          <div class="tel-input">
            <TextInput v-model="tel" type="tel" :disabled="isBlock" placeholder="電話番号1" />
            <p v-if="tel1Dup" class="tel-dup">
              ※この番号に紐づく顧客情報が複数あります
            </p>
          </div>
          <Button small fit :disabled="!hasCustomerTel" @click="searchTel"><SearchIcon /></Button>
          <div class="tel-input">
            <TextInput v-model="tel2" type="tel" :disabled="isBlock" placeholder="電話番号2" />
            <p v-if="tel2Dup" class="tel-dup">
              ※この番号に紐づく顧客情報が複数あります
            </p>
          </div>
        </div>
      </FormLabel>
      <FormLabel itemName="会社名">
        <TextInput v-model="officeName" :disabled="isBlock" />
      </FormLabel>
    </FormSection>
    <CustomerSelectModal
      v-if="customerSelectModal"
      :condition="customerSelectModal"
      @select="selectCustomer"
      @close="customerSelectModal = false"
    />

    <FormSection v-if="!showDetails">
      <a class="show-details" @click="$store.commit('reserves/edit/setShowDetails', true)"
        >詳細内容を入力する<ArrowLightIcon class="allow"
      /></a>
    </FormSection>

    <FormSection v-else>
      <FormLabel itemName="コース">
        <div class="courses">
          <div v-for="(course, i) in courses" :key="i" class="course">
            <TextInput
              :value="courseName(course)"
              placeholder="コースなし"
              @focus="showCourseModal($event, i)"
              @click="showCourseModal($event, i)"
              :disabled="isBlock || hasCoupon || isOrganizerPoint"
            />
            <NumericInput
              class="count"
              defaultVal="1"
              :value="course.count || (isBlock || hasCoupon ? 0 : 1)"
              :disabled="!course.id || hasCoupon || isOrganizerPoint"
              :maxDigit="4"
              @update="updateCourse(i, { ...course, count: $event })"
            />
            名
          </div>
          <span v-if="hasCoupon || isOrganizerPoint" class="note">
            {{
              hasCoupon
                ? '※ぐるなびネット予約クーポン利用予約の場合、コース変更・コース数量変更はできません'
                : '※幹事プログラム対象の予約の場合、コース変更・コース数量変更はできません'
            }}
          </span>
          <Button
            v-if="canAddCourses"
            x-small
            fit
            @click="addCourse"
            :disabled="isBlock || hasCoupon || isOrganizerPoint"
            :class="{ disabled: isBlock }"
          >
            <template #icon><AddIcon /></template>追加
          </Button>
        </div>
      </FormLabel>
      <CourseSelectModal
        v-if="courseSelectModal"
        v-bind="courseSelectModal"
        @select="updateCourse"
        @close="courseSelectModal = null"
      />

      <div class="reset-time">
        <ResetTimeInput />
      </div>

      <div class="form-item type-media">
        <ReservationTypeInput />
        <ReservationMediaInput />
      </div>
      <div class="purpose-settlement">
        <PurposeInput />
        <SettlementInput class="settlement" :reserve="reservation" />
      </div>

      <div v-if="hasCoupon" class="coupon">
        <NetReservationCoupon />
      </div>

      <div class="remarks">
        <RemarksInput />
      </div>

      <FormLabel itemName="予約タグ">
        <div class="tags">
          <ToggleButton
            v-for="(tag, i) in allTagsWithUsingTags"
            :key="i"
            :class="['tag', { deleted: isDeletedTag(tag) }]"
            :value="tag"
            v-model="tags"
            :disabled="isBlock"
          >
            <TagIcon v-if="tag.icon && !isBlock" :id="tag.icon" style="height: 16px; width: 16px;" />
            {{ tag.name }}
          </ToggleButton>
        </div>
      </FormLabel>

      <FormLabel itemName="ご予約名">
        <TextInput v-model="reserveName" :disabled="isBlock" />
      </FormLabel>

      <FormLabel itemName="メールアドレス">
        <div class="mail">
          <TextInput v-model="mail1" :disabled="isBlock" placeholder="メール1" />
          <TextInput v-model="mail2" :disabled="isBlock" placeholder="メール2" />
          <p v-if="isDaicho" class="note">※入力がない場合はお客様宛のご予約リマインドメールが不達になります</p>
        </div>
      </FormLabel>
    </FormSection>

    <template #footer>
      <Button @click="cancel">キャンセル</Button>
      <StaffSelectPopover
        :boundariesElement="null"
        @update:open="confirmDisabled = $event"
        @select="confirm"
        :disabled="!isBusinessHours || hasCourseAlert"
      >
        <Button primary :disabled="isConfirmDisabled" @click="onConfirmSelect">確認</Button>
      </StaffSelectPopover>
    </template>
    <BusinessHoursAlertModal v-if="BusinessHoursAlertModal" @confirm="confirm" @close="closeBusinessHoursAlertModal" />
  </SpPage>
</template>

<script>
import { addMinutes, addDays } from 'date-fns'
import { differenceWith, isEqual, pick, sum, isEmpty } from 'lodash'
import { mapState, mapGetters } from 'vuex'
import { today, toHourClock } from '@/utils/date'
import { toMinutes, minutesToTime, eachTime, minutesToJpNotation } from '@/utils/time'
import { isTimeOverlap, unMatchedTableTypes, isResrakuReserve, reserveFooterAlert } from '@/utils/reserve'
import { isDaicho, isGnavi } from '@/resources/reservation/syncTypeMap'
import { isBlock } from '@/resources/reservation/typeMap'
import { isOrganizerPoint } from '@/resources/reservation/registerTypeMap'
import { models } from '@/utils/vue'
import SpPage from '@/components/templates/SpPage'
import Validation from '@/components/atoms/Validation'
import SelectBox from '@/components/atoms/SelectBox'
import Button from '@/components/atoms/Button'
import TextInput from '@/components/atoms/TextInput'
import ToggleButton from '@/components/atoms/ToggleButton'
import CalendarInput from '@/components/molecules/CalendarInput'
import FormLabel from '@/components/molecules/FormLabel'
import FormSection from '@/components/molecules/FormSection'
import Dialog from '@/components/molecules/ModalDialog'
import CourseSelectModal from '@/components/organisms/CourseSelectModal'
import CustomerSelectModal from '@/components/organisms/CustomerSelectModal'
import BusinessHoursAlertModal from '@/components/organisms/BusinessHoursAlertModal'
import StaffSelectPopover from '@/components/organisms/StaffSelectPopover'
import TableSelectModal from '@/components/organisms/TableSelectModal'
import CustomerTypeInput from '@/pages/reservationEdit/components/Detail/customer/CustomerTypeInput'
import ResetTimeInput from '@/pages/reservationEdit/components/Detail/reservation/ResetTimeInput'
import ReservationTypeInput from '@/pages/reservationEdit/components/Detail/reservation/ReservationTypeInput'
import ReservationMediaInput from '@/pages/reservationEdit/components/Detail/reservation/ReservationMediaInput'
import PurposeInput from '@/pages/reservationEdit/components/Detail/reservation/PurposeInput'
import NetReservationCoupon from '@/pages/reservationEdit/components/Detail/reservation/NetReservationCoupon'
import RemarksInput from '@/pages/reservationEdit/components/Detail/reservation/RemarksInput'
import SettlementInput from '@/pages/reservationEdit/components/Detail/reservation/SettlementInput'
import NumericInput from '@/components/molecules/NumericInput'
import AddIcon from '@/components/icons/add'
import ArrowLightIcon from '@/components/icons/arrowLight'
import ClearIcon from '@/components/icons/clear'
import SearchIcon from '@/components/icons/search'
import TagIcon from '@/components/icons/TagIcon'

export default {
  components: {
    SpPage,
    Validation,
    SelectBox,
    Button,
    TextInput,
    ToggleButton,
    CalendarInput,
    FormLabel,
    FormSection,
    CourseSelectModal,
    CustomerSelectModal,
    BusinessHoursAlertModal,
    StaffSelectPopover,
    TableSelectModal,
    CustomerTypeInput,
    ResetTimeInput,
    ReservationTypeInput,
    ReservationMediaInput,
    PurposeInput,
    NetReservationCoupon,
    RemarksInput,
    SettlementInput,
    NumericInput,
    AddIcon,
    ArrowLightIcon,
    ClearIcon,
    SearchIcon,
    TagIcon,
  },
  props: {
    id: Number,
    backTo: String,
  },
  data() {
    return {
      courseSelectModal: null,
      customerSelectModal: null,
      tableSelectModal: false,
      BusinessHoursAlertModal: false,
      confirmDisabled: false,
      lastChangePeople: [],
    }
  },
  computed: {
    ...mapState({
      reservation: state => state.reserves.edit.reservation,
      customerDetail: state => state.reserves.edit.customerDetail,
      detail: state => state.reserves.detail.reservation,
      tables: state => state.tables,
      syncGnavis: state => state.syncGnavis,
      busy: state => state.reserves.detail.busy,
      showDetails: state => state.reserves.edit.showDetails,
      businessDay: state => state.businessDay.list,
      carendarBusy: state => state.init.busy,
      reserveCalendar: state => state.init.reserveCalendar,
    }),
    ...mapGetters({
      changed: 'reserves/edit/changed',
      reserveDatetime: 'reserves/edit/reserveDatetime',
      reserve: 'reserves/edit/reserve',
      defaultUtilizationTime: 'reserves/edit/defaultUtilizationTime',
      hasCustomerName: 'reserves/edit/hasCustomerName',
      hasCustomerTel: 'reserves/edit/hasCustomerTel',
      isWalkin: 'reserves/edit/isWalkin',
      isReservedByGnavi: 'reserves/detail/isReservedByGnavi',
      isNumberExceeded: 'reserves/edit/isNumberExceeded',
      isBlockOverTime: 'reserves/edit/isBlockOverTime',
      disableEditDateReservedByGnavi: 'reserves/edit/disableEditDateReservedByGnavi',
      disableEditTimeReservedByGnavi: 'reserves/edit/disableEditTimeReservedByGnavi',
      isBusinessHours: 'reserves/edit/isBusinessHours',
      reservesByTableId: 'reserves/byTableId',
      allTables: 'tables/all',
      allHolidays: 'holidays/all',
      allCourses: 'courses/all',
      allTags: 'tags/all',
    }),
    ...models(
      [
        'adults',
        'children',
        'resetTime',
        'type',
        'media',
        'purpose',
        'settlement',
        'remarks',
        'customer',
        'tags',
        'reserveName',
        'tableIds',
      ],
      ({ reservation }, key) => reservation[key],
      ({ $store }, key, value) =>
        $store.commit('reserves/edit/update', { [key]: key === 'reserveName' ? value.trim() : value }),
    ),
    ...models(
      [
        'lastNameKana',
        'firstNameKana',
        'lastName',
        'firstName',
        'tel',
        'tel1DuplicationFlag',
        'tel2',
        'tel2DuplicationFlag',
        'officeName',
        'mail1',
        'mail2',
      ],
      ({ reservation }, key) => reservation.customer[key],
      ({ $store }, key, value) => $store.commit('reserves/edit/update', { customer: { [key]: value.trim() } }),
    ),
    reserveDate: {
      get() {
        return this.reserveDatetime.date
      },
      set(date) {
        const { commit, dispatch } = this.$store
        commit('reserves/edit/update', { reserveDatetime: addMinutes(date, toMinutes(this.reserveTime)) })
        dispatch('stores/zone/get', date)
        dispatch('reserves/get', { date: this.reserveDate })
      },
    },
    minDate() {
      return this.id ? today() : null
    },
    maxDate() {
      return this.isReservedByGnavi ? toHourClock(addDays(new Date(), 89)).date : null
    },
    reserveTime: {
      get() {
        return this.reserveDatetime.time
      },
      set(time) {
        this.$store.commit('reserves/edit/update', {
          reserveDatetime: addMinutes(this.reserveDate, toMinutes(time)),
        })
      },
    },
    utilizationTime: {
      get() {
        return this.reserve.utilizationTime || this.defaultUtilizationTime
      },
      set(time) {
        this.$store.commit('reserves/edit/update', { utilizationTime: time })
      },
    },
    courses: {
      get() {
        return this.reserve.courses.length ? this.reserve.courses : [{}]
      },
      set(courses) {
        this.$store.commit('reserves/edit/update', { courses })
      },
    },
    tel1Dup() {
      return this.reservation.id && this.tel === this.customerDetail.tel1 && this.tel1DuplicationFlag
    },
    tel2Dup() {
      return this.reservation.id && this.tel2 === this.customerDetail.tel2 && this.tel2DuplicationFlag
    },
    startTimeMap() {
      return eachTime('6:00', '29:45').reduce((o, time) => Object.assign(o, { [time]: time }), {})
    },
    endTimeMap() {
      const startTime = toMinutes(this.reserveTime)
      return eachTime(minutesToTime(startTime + 15), minutesToTime(startTime + 24 * 60)).reduce(
        (o, time, i) => Object.assign(o, { [(i + 1) * 15]: `${time}（${minutesToJpNotation((i + 1) * 15)}）` }),
        {},
      )
    },
    peopleMap() {
      return [...Array(1000).keys()]
    },
    tableNameList() {
      if (!this.tableIds.length) return []
      if (this.tableIds[0] === -1) return ['テーブル未指定']
      return this.tableIds.map(id => (this.tables.byId[id] || {}).name)
    },
    deficiency() {
      return this.getDeficiency()
    },
    hasDuplicate() {
      return this.tableIds.filter(id => id !== -1 && this.getDuplicatedReserves(id).length).length
    },
    unMatchedTableOptions() {
      return unMatchedTableTypes(this.reserve, this.tables, this.syncGnavis)
    },
    tableSelectErrorMsg() {
      return [
        this.deficiency ? `${this.deficiency}席不足しています` : '',
        this.hasDuplicate ? '予約が重複しています' : '',
        this.unMatchedTableOptions.length
          ? `[ ${reserveFooterAlert(this.unMatchedTableOptions)} ] 指定外が選択されています`
          : '',
      ].filter(o => o)
    },
    allTagsWithUsingTags() {
      return this.isModify ? this.deletedTags.concat(this.allTags) : this.allTags
    },
    deletedTags() {
      return differenceWith(this.detail.tags, this.allTags, isEqual)
    },
    canAddCourses() {
      return this.courses.length < Math.min(this.allCourses.length + 1, 10)
    },
    isModify() {
      return !!this.id
    },
    isConfirmDisabled() {
      const hasEmptyRequired =
        !(
          this.reserveDate &&
          this.reserveTime &&
          this.utilizationTime &&
          (this.isBlock || this.adults || this.children)
        ) || !this.tableIds.length
      const hasEmptyContact =
        !this.isBlock &&
        !this.isWalkin &&
        !(this.lastNameKana || this.lastName || this.firstNameKana || this.firstName || this.tel || this.tel2)
      return this.confirmDisabled || this.isNumberExceeded || hasEmptyRequired || hasEmptyContact
    },
    isDaicho() {
      return isDaicho(this.reservation.syncType)
    },
    isBlock() {
      return isBlock(this.reserve.type)
    },
    hasCourseAlert() {
      return (
        !isEqual(this.lastChangePeople, [this.reservation.adults, this.reservation.children]) &&
        this.courses.some(course => !isEmpty(course) && !!course.id)
      )
    },
    isResrakuReserve() {
      return isResrakuReserve(this.reserve)
    },
    isTableSerectOnly() {
      return this.isResrakuReserve.hotpepper || this.isResrakuReserve.inquiry || this.isResrakuReserve.retty
    },
    hasCoupon() {
      return !!this.reservation.coupon
    },
    isOrganizerPoint() {
      // 幹事ポイント対象
      return isOrganizerPoint(this.reservation.registerPointType)
    },
  },
  watch: {
    changed() {
      this.$store.commit('app/editing', this.changed)
    },
  },
  async mounted() {
    const { dispatch, commit } = this.$store
    await Promise.all([
      dispatch('syncGnavis/get', { readonly: true }),
      dispatch('tables/get', { readonly: true }),
      dispatch('courses/get'),
      dispatch('tags/get'),
      dispatch('staffs/get'),
      dispatch('stores/zone/get', this.reserveDate),
      dispatch('holidays/get'),
      dispatch('businessDay/get'),
      dispatch('reserves/get', { date: this.reserveDate, readonly: true }),
    ])
    commit('reserves/edit/setCustomerDetail', {
      tel1: this.reservation.customer.tel,
      tel2: this.reservation.customer.tel2,
    })
    this.getDeficiency()
    this.lastChangePeople = [this.reservation.adults, this.reservation.children]
    if (this.isTableSerectOnly) this.tableSelectModal = true
  },
  beforeDestroy() {
    this.$store.commit('app/editing', false)
  },
  methods: {
    clearTables() {
      this.tableIds = []
      this.getDeficiency()
    },
    updateTables(table) {
      this.tableIds = table
      const tables = table.map((id, i) => ({ id, name: this.tableNameList[i] }))
      this.$store.commit('reserves/edit/update', { tables })
      this.getDeficiency()
    },
    getDeficiency(selectedTableIds = this.tableIds) {
      const { adults, children } = this.reserve
      const tableIds = selectedTableIds || []
      const availableSeat = sum(
        tableIds.map(id => (this.allTables.find(table => table.id === id) || {}).maxAvailableSeat),
      )
      return tableIds.includes(-1) ? 0 : Math.max(adults + children - availableSeat, 0)
    },
    getDuplicatedReserves(id) {
      const reserves = this.reservesByTableId[id] && this.reservesByTableId[id].filter(r => r.id !== this.reserve.id)
      if (!reserves) return []
      const duplicatedReserves = reserves
        .map(reserve => (isTimeOverlap(reserve, this.reserve) ? reserve : false))
        .filter(v => v)
      return duplicatedReserves
    },
    searchName() {
      this.customerSelectModal = {
        lastNameKana: this.lastNameKana,
        lastName: this.lastName,
        firstNameKana: this.firstNameKana,
        firstName: this.firstName,
      }
    },
    searchTel() {
      this.customerSelectModal = { tel: this.tel, tel2: this.tel2 }
    },
    selectCustomer({ id }) {
      this.$store.dispatch('reserves/edit/attachCustomer', id)
    },
    clearCustomer() {
      this.customer = {
        id: null,
        firstName: null,
        firstNameKana: null,
        lastName: null,
        lastNameKana: null,
        tel: null,
        tel1DuplicationFlag: false,
        tel2: null,
        tel2DuplicationFlag: false,
        officeName: null,
        mail1: null,
        mail2: null,
      }
      this.$store.commit('reserves/edit/setCustomerDetail', {})
    },
    addCourse() {
      this.courses = this.courses.concat({})
    },
    courseName(course) {
      if (!course.name) return ''
      if (isGnavi(this.reservation.syncType)) return `${course.name} / ¥${course.price.toLocaleString()}`
      return course.name
    },
    showCourseModal(e, index) {
      e.target.blur()
      this.courseSelectModal = { index }
    },
    updateCourse(index, course) {
      this.$store.commit('reserves/edit/updateCourse', {
        index,
        course: {
          ...pick(course, ['id', 'price']),
          name: course.names?.jpn || course.name,
          count: +course.count || this.courses[index].count || (index === 0 ? this.adults + this.children : 1),
        },
      })
    },
    async cancel() {
      if (!this.changed || (await Dialog.confirmLeaveReserve())) {
        this.$store.commit('app/editing', false)
        this.$router.push(this.backTo)
      }
    },
    isDeletedTag(tag) {
      return this.deletedTags.includes(tag)
    },
    async onConfirmSelect() {
      if (!this.hasCoupon && this.hasCourseAlert && !this.isOrganizerPoint) {
        await Dialog.show({
          message: '予約人数が変更されています。\nコース数をご確認ください。',
          okText: '閉じる',
        })
      }
      this.lastChangePeople = [this.reservation.adults, this.reservation.children]
      this.confirmDisabled = false

      if (!this.isBusinessHours) this.BusinessHoursAlertModal = true
    },
    closeBusinessHoursAlertModal() {
      this.BusinessHoursAlertModal = false
      this.confirmDisabled = false
    },
    async confirm({ id }) {
      if (await this.$store.dispatch('reserves/edit/confirm', id)) {
        this.$router.push({
          name: this.id ? 'ReserveEditConfirm' : 'ReserveNewConfirm',
          query: { backTo: this.backTo },
        })
      }
    },
    async setDate(date) {
      await this.$store.dispatch('init/createCalendar', { date })
    },
  },
}
</script>

<style lang="postcss" scoped>
.customer-type {
  display: flex;
  flex-shrink: 0;
  justify-content: center;
  padding: 8px 0;
}

.form-item {
  display: grid;
  grid-auto-flow: column;

  &.couple {
    grid-gap: 8px;
  }

  &.table {
    font-size: 15px;
    font-weight: bold;
    grid-auto-flow: row;
    grid-gap: 16px;
  }
}

.number-error {
  margin-top: -8px;
}

.block-error {
  color: var(--pure-red);
  font-weight: 600;
  line-height: 19px;
  margin-top: -8px;
}

.buttons {
  display: flex;

  .button {
    font-size: 15px;
    width: 80px;
  }

  .button + .button {
    margin-left: 8px;
  }
}

>>> .calendar-button {
  font-size: 15px;
}

.time {
  grid-gap: 8px;
  grid-template-columns: 1fr min-content 1fr;

  span {
    align-self: flex-end;
    line-height: 40px;
  }
}

.table-list {
  display: flex;
  flex-wrap: wrap;
  margin: 0 -8px;

  li {
    margin: 0 8px;
  }
}

.error {
  color: var(--pure-red);
}

.names {
  display: grid;
  grid-gap: 16px 8px;
  grid-template-columns: 1fr 1fr auto;
}

.tels {
  display: grid;
  grid-gap: 16px 8px;
  grid-template-columns: 1fr auto;

  .tel-input {
    display: grid;
    row-gap: 8px;
  }

  .tel-dup {
    color: var(--pure-red);
    font-size: 13px;
    font-weight: 600;
  }
}

.customer-clear {
  align-items: center;
  color: var(--brand-color);
  cursor: pointer;
  display: grid;
  fill: var(--brand-color);
  font-size: 12px;
  font-weight: 300;
  grid-auto-flow: column;
  grid-gap: 4px;
  justify-self: flex-end;
}

.show-details {
  display: flex;
  font-weight: 600;
  justify-content: space-between;
}

.courses {
  display: grid;
  grid-gap: 8px;
  .disabled svg {
    fill: var(--very-light-gray);
  }
}

.course {
  align-items: flex-end;
  display: grid;
  grid-gap: 8px;
  grid-template-columns: 1fr 72px auto;
}

.course >>> .count input {
  text-align: right;
}

.purpose-settlement,
.reset-time {
  >>> dd {
    label {
      width: 100%;
    }
  }
}

.type-media {
  display: grid;
  grid-auto-columns: 1fr 1fr;
  grid-gap: 8px;

  >>> dd {
    label {
      width: 100%;
    }
  }
}

.disabled >>> .radio-list > label {
  border-color: var(--very-light-gray);
}

.coupon,
.remarks {
  >>> textarea {
    border: 1px solid var(--border-color);
    border-radius: 4px;
    box-sizing: border-box;
    display: block;
    font-size: 13px;
    margin-top: 8px;
    padding: 16px;
    resize: none;
    width: 100%;
  }
}

.reset-time,
.type-media,
.purpose-settlement,
.coupon,
.remarks {
  >>> dt {
    font-size: 13px;
    font-weight: 600;
    line-height: 1;
  }
}

.settlement {
  margin-top: 16px;
  >>> .coupon-point {
    color: var(--very-dark-gray-2);
  }
}

.tags {
  display: flex;
  flex-wrap: wrap;
  margin: -4px;

  .toggle-button {
    line-height: normal;
  }
}

.tag {
  margin: 4px;
  width: auto;

  &.deleted {
    >>> .label {
      text-decoration: line-through;
    }
  }
}

.mail {
  display: grid;
  grid-gap: 16px;
}

.note {
  font-size: 11px;
  font-weight: 400;
}
</style>
